import React, { useEffect, useMemo, useState } from 'react';
import { alpha } from '@mui/material/styles';
import {
  Alert,
  Box,
  Chip,
  Divider,
  Stack,
  Typography,
  CircularProgress,
} from '@mui/material';
import { Button, Input, EmptyState, SearchableSelect } from '../../../../components/ui';
import AnalyticsChartRenderer from '../charts/AnalyticsChartRenderer';
import ScopeFields from './ScopeFields';
import ScopeContextBanner from './ScopeContextBanner';
import VisualizationSelect from './VisualizationSelect';
import ReportMetricFields from './ReportMetricFields';
import ReportFiltersPanel from './ReportFiltersPanel';
import LivePreviewPanel from './LivePreviewPanel';
import ReportDateRange from './ReportDateRange';
import { ANALYTICS_COPY } from '../../constants/analyticsCopy';
import { formatFilterSummary } from '../../utils/reportFilters';
import { buildReportSentence } from '../../utils/measureFieldUtils';
import useAnalyticsForms from '../../hooks/useAnalyticsForms';
import { normalizeWhatsappTemplateOptions } from '../../utils/templateOptions';
import { analyticsService } from '../../services/analyticsService';
import { templateService } from '../../../whatsapp-templates/services/templateService';
import { parseTemplateListResponse } from '../../../whatsapp-templates/adapters/templateListAdapter';
import { mapBotToSelectOption } from '../../../whatsapp-templates/utils/normalizeTemplate';
import {
  getChartConfig,
  resolvePreviewVisualization,
} from '../../utils/chartTypeMapping';
import { resolveChartAxisLabels } from '../../utils/chartAxisLabels';
import { DATASET_WHATSAPP, DATASET_INTERVENE, usesIndependentScope } from '../../constants/datasetScope';
import { CHART_TYPES } from '../../constants/chartTypes';
import { patchForChartTypeChange } from '../../utils/chartTypeChange';
import InterveneAudiencePanel from './InterveneAudiencePanel';
import useInterveneOrgAgents from '../../hooks/useInterveneOrgAgents';
import { readInterveneAudienceFilters } from '../../utils/interveneFilters';
import {
  buildInterveneAudienceCaption,
  buildIntervenePreviewTitle,
  resolveFocusedUserDisplayName,
} from '../../utils/intervenePreviewLabels';

function countPreviewCategories(preview) {
  const result = preview?.result;
  if (!result) return 0;
  if (Array.isArray(result.categories)) return result.categories.length;
  if (Array.isArray(result.series?.[0]?.data)) return result.series[0].data.length;
  return 0;
}

export default function StepConfigureAndPreview({
  dataset = '',
  datasetLabel = '',
  businessFunctions = [],
  accountId,
  campaignIds = [],
  leadsFormIds = [],
  scopeLocked = false,
  scopeContext = {},
  onScopeChange,
  metrics = [],
  groupByOptions = [],
  filterFieldOptions = [],
  measureFieldOptions = [],
  timeFieldOptions = [],
  needsTableColumns = false,
  isLoadingFormFields = false,
  builderState,
  onChange,
  preview,
  previewError = '',
  isPreviewLoading,
  scopeLoading = false,
  needsOrganisation = false,
  organisationId = '',
  querySpec = null,
  canExport = false,
  onExport,
  onSave,
  canSave = false,
  isEditMode = false,
  sx,
}) {
  const {
    chartType,
    metric,
    aggregation = 'count',
    measureField = '',
    dimension,
    filters = [],
    includedValues = [],
    tableColumns = [],
    dateRange = {},
    templateId = '',
    botNumber = '',
    reportName,
    detailLevel = 'standard',
  } = builderState;

  const { forms } = useAnalyticsForms(dataset, campaignIds);

  const scopeLabels = useMemo(() => {
    const bf = businessFunctions.find((item) => item.account_id === accountId);
    const selectedCampaigns = (bf?.campaigns || []).filter((item) => campaignIds.includes(item.campaign_id));
    const selectedForms = forms.filter((item) => leadsFormIds.map(String).includes(String(item.leads_form_id)));
    const serviceName = selectedCampaigns.length > 1
      ? selectedCampaigns.map((item) => item.label).join(', ')
      : selectedCampaigns[0]?.label || scopeContext.serviceName || '';
    const formName = selectedForms.length > 1
      ? selectedForms.map((item) => item.label).join(', ')
      : selectedForms[0]?.label || scopeContext.formName || '';
    return {
      businessFunctionName: bf?.label || scopeContext.businessFunctionName || '',
      serviceName,
      formName,
    };
  }, [businessFunctions, accountId, campaignIds, scopeContext, forms, leadsFormIds]);

  const chartConfig = getChartConfig(chartType);
  const previewDimension = chartType === 'table'
    ? dimension
    : (dimension || (chartConfig.requiresDimension ? groupByOptions[0]?.id || '' : ''));
  const tableName = scopeLabels.formName;
  const isWhatsApp = dataset === DATASET_WHATSAPP;
  const isIntervene = dataset === DATASET_INTERVENE;
  const usesBusinessScope = !usesIndependentScope(dataset);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [botOptions, setBotOptions] = useState([]);
  const [isLoadingBots, setIsLoadingBots] = useState(false);
  const [rawTemplates, setRawTemplates] = useState([]);
  const scopeReady = usesBusinessScope
    ? Boolean(accountId) && businessFunctions.length > 0
    : true;

  useEffect(() => {
    if (!isWhatsApp || !organisationId) return;
    let cancelled = false;

    async function loadBots() {
      setIsLoadingBots(true);
      try {
        const res = await templateService.getBotNumbers(organisationId);
        if (cancelled) return;
        const rawList = Array.isArray(res.data) ? res.data : res.data?.list ?? res.data?.results ?? [];
        setBotOptions(rawList.map(mapBotToSelectOption).filter(Boolean));
      } catch {
        // ignore
      } finally {
        if (!cancelled) setIsLoadingBots(false);
      }
    }

    loadBots();
    return () => { cancelled = true; };
  }, [isWhatsApp, organisationId]);

  useEffect(() => {
    if (!isWhatsApp) return;

    let cancelled = false;
    async function loadTemplates() {
      setIsLoadingTemplates(true);

      // 1. Try templateService.getTemplates with botNumber
      try {
        const res = await templateService.getTemplates({
          organisation: organisationId,
          page: 0,
          limit: 200,
          archived: 'false',
          botNumber: botNumber || undefined,
        });
        if (cancelled) return;
        const parsed = parseTemplateListResponse(res);
        const list = parsed?.templates || res?.data?.data || res?.data;
        if (Array.isArray(list)) {
          setRawTemplates(list);
          const normalized = normalizeWhatsappTemplateOptions(list, '');
          const items = normalized.filter((item) => Boolean(item.id));
          if (items.length > 0) {
            setTemplateOptions(items);
            setIsLoadingTemplates(false);
            return;
          }
        }
      } catch {
        // Fallback
      }

      // 2. Try previewTemplates if available
      const previewTemplates = preview?.result?.templates;
      if (Array.isArray(previewTemplates) && previewTemplates.length > 0) {
        setTemplateOptions(
          previewTemplates.map((t) => ({ id: String(t.id), label: t.label || String(t.id) }))
        );
        setIsLoadingTemplates(false);
        return;
      }

      // 3. Try analyticsService.getTemplates
      try {
        const res = await analyticsService.getTemplates(organisationId);
        if (cancelled) return;
        const normalized = normalizeWhatsappTemplateOptions(res?.data || res, '');
        const items = normalized.filter((item) => Boolean(item.id));
        if (items.length > 0) {
          setTemplateOptions(items);
        }
      } catch {
        // Ignore
      } finally {
        if (!cancelled) setIsLoadingTemplates(false);
      }
    }

    loadTemplates();
    return () => { cancelled = true; };
  }, [isWhatsApp, organisationId, botNumber]);

  const effectiveBotOptions = useMemo(() => {
    const map = new Map();
    botOptions.forEach((b) => {
      if (b.id) map.set(String(b.id), b);
    });
    rawTemplates.forEach((t) => {
      const bNum = t.bot_number || t.botNumber;
      if (bNum && !map.has(String(bNum))) {
        map.set(String(bNum), { id: String(bNum), label: String(bNum), value: String(bNum) });
      }
    });
    return Array.from(map.values());
  }, [botOptions, rawTemplates]);

  const filteredTemplateOptions = useMemo(() => {
    if (!botNumber) return templateOptions;
    if (!rawTemplates.length) return templateOptions;
    const matching = rawTemplates.filter(
      (t) => String(t.bot_number || t.botNumber || '') === String(botNumber)
    );
    const normalized = normalizeWhatsappTemplateOptions(matching, '');
    const items = normalized.filter((item) => Boolean(item.id));
    return items.length > 0 ? items : templateOptions;
  }, [botNumber, templateOptions, rawTemplates]);

  const AUDIENCE_FIELD_IDS = useMemo(() => ['response_agent_username', 'agent_user_id', 'phone_number'], []);
  const audienceFilters = useMemo(
    () => (filters || []).filter((item) => AUDIENCE_FIELD_IDS.includes(item.field)),
    [filters, AUDIENCE_FIELD_IDS]
  );
  const advancedFilters = useMemo(
    () => (filters || []).filter((item) => !AUDIENCE_FIELD_IDS.includes(item.field)),
    [filters, AUDIENCE_FIELD_IDS]
  );

  const previewCategoryCount = countPreviewCategories(preview);
  const previewVisualization = resolvePreviewVisualization(chartType, preview?.visualization);
  const { agents: interveneAgents } = useInterveneOrgAgents(isIntervene ? organisationId : '');
  const { agent: focusedAgentUsername } = readInterveneAudienceFilters(filters);
  const focusedUserDisplayName = useMemo(
    () => resolveFocusedUserDisplayName(focusedAgentUsername, interveneAgents),
    [focusedAgentUsername, interveneAgents]
  );

  const chartLabels = useMemo(
    () => ({
      ...resolveChartAxisLabels({
        chartType,
        dimension: previewDimension,
        metric,
        aggregation,
        measureField,
        groupByOptions,
        measureFieldOptions,
        metrics,
        timeFieldOptions,
      }),
      focusedUserDisplayName: isIntervene ? focusedUserDisplayName : '',
      ...(isWhatsApp && chartType === 'line' ? { yAxisLabel: 'Messages' } : {}),
      columnLabels: Object.fromEntries(
        groupByOptions.map((item) => [String(item.id).replace(/\./g, '__'), item.label])
      ),
    }),
    [
      chartType,
      previewDimension,
      metric,
      aggregation,
      measureField,
      groupByOptions,
      measureFieldOptions,
      metrics,
      timeFieldOptions,
      isIntervene,
      isWhatsApp,
      focusedUserDisplayName,
    ]
  );

  if (needsOrganisation) {
    return (
      <Box sx={sx?.sectionCard}>
        <EmptyState
          title={ANALYTICS_COPY.scopeSelectOrgTitle}
          description={ANALYTICS_COPY.scopeSelectOrgDescription}
        />
      </Box>
    );
  }

  if (usesBusinessScope && scopeLoading) {
    return (
      <Stack alignItems="center" spacing={1.5} py={6}>
        <CircularProgress size={32} />
        <Typography variant="body2" color="text.secondary">
          {ANALYTICS_COPY.scopeLoading}
        </Typography>
      </Stack>
    );
  }

  if (usesBusinessScope && !businessFunctions.length) {
    return (
      <Box sx={sx?.sectionCard}>
        <EmptyState
          title={ANALYTICS_COPY.scopeEmptyTitle}
          description={ANALYTICS_COPY.scopeEmptyDescription}
        />
      </Box>
    );
  }

  const previewContent = () => {
    const emptyMessage = (text) => (
      <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
        {text}
      </Typography>
    );

    if (!scopeReady) {
      return emptyMessage(ANALYTICS_COPY.previewNeedsScope);
    }
    const requiresDimension = chartConfig.requiresDimension && !isWhatsApp;
    if (requiresDimension && !previewDimension) {
      if (isIntervene) {
        return emptyMessage(ANALYTICS_COPY.intervenePreviewNeedsDimension);
      }
      return emptyMessage(ANALYTICS_COPY.previewNeedsDimension);
    }
    if (isPreviewLoading) {
      return (
        <Stack alignItems="center" spacing={1.5} py={5}>
          <CircularProgress size={32} thickness={4} />
          <Typography variant="body2" color="text.secondary">
            {ANALYTICS_COPY.previewLoading}
          </Typography>
        </Stack>
      );
    }
    if (previewError) {
      return (
        <Alert severity="error" sx={{ borderRadius: 1.5 }}>
          {previewError}
        </Alert>
      );
    }
    if (preview?.result) {
      const hasData = preview.result.kind === 'number'
        || (preview.result.categories?.length)
        || (preview.result.series?.[0]?.data?.length)
        || (preview.result.rows?.length);
      if (!hasData) {
        return emptyMessage(ANALYTICS_COPY.previewNoData);
      }
      return (
        <AnalyticsChartRenderer
          result={preview.result}
          visualization={previewVisualization}
          chartLabels={chartLabels}
          compact
          sx={sx}
          querySpec={querySpec}
          orgId={organisationId}
        />
      );
    }
    return emptyMessage(ANALYTICS_COPY.previewEmpty);
  };

  const categoryLabel =
    groupByOptions.find((item) => item.id === previewDimension)?.label || '';
  const measureFieldLabel = measureFieldOptions.find((item) => item.id === measureField)?.label || '';
  const selectedTemplate = templateOptions.find((item) =>
    String(item.id) === String(templateId) || String(item.label) === String(templateId)
  ) || (templateId ? { id: String(templateId), label: String(templateId) } : null);
  const catalogMetricLabel = metrics.find((item) => item.id === metric)?.label || '';
  const tableColumnLabels = (tableColumns.length ? tableColumns : (dimension ? [dimension] : []))
    .map((id) => groupByOptions.find((item) => item.id === id)?.label)
    .filter(Boolean);
  const previewTitle = isWhatsApp
    ? (selectedTemplate?.label || ANALYTICS_COPY.whatsappTemplateAll)
    : isIntervene
      ? buildIntervenePreviewTitle({
        metricLabel: catalogMetricLabel || 'Overview',
        focusedUserDisplayName,
        categoryLabel,
        showGroupBy: chartConfig.showGroupBy,
      })
      : chartType === 'table'
        ? (tableColumnLabels.join(', ') || ANALYTICS_COPY.tableColumnsLabel)
        : buildReportSentence({
          chartType,
          aggregation,
          measureFieldLabel,
          categoryLabel: chartConfig.showGroupBy ? categoryLabel : '',
          includedValues: chartConfig.showGroupBy ? includedValues : [],
        });
  const audienceCaption = isIntervene
    ? buildInterveneAudienceCaption(filters, interveneAgents)
    : '';
  const scopeCaption = [
    scopeLabels.serviceName ? `Campaign: ${scopeLabels.serviceName}` : '',
    scopeLabels.formName ? `Form: ${scopeLabels.formName}` : '',
  ].filter(Boolean).join(' · ');
  const extraFilterCaption = [
    scopeCaption,
    audienceCaption,
    ...filters
      .filter((item) => item.field && String(item.value ?? '').trim())
      .filter((item) => !['agent_user_id', 'response_agent_username', 'phone_number'].includes(item.field))
      .map((item) => formatFilterSummary(item, filterFieldOptions)),
  ].filter(Boolean).join(' · ');

  const handleChartTypeChange = (nextChartType) => {
    const patch = patchForChartTypeChange(builderState, nextChartType);
    if (getChartConfig(nextChartType).requiresDimension && !dimension) {
      const defaultDimension = groupByOptions[0]?.id;
      if (defaultDimension) patch.dimension = defaultDimension;
    }
    onChange(patch);
  };

  const selectedChart = CHART_TYPES.find((item) => item.id === chartType);
  const filtersCollapsed = chartType === 'number' || chartConfig.showGroupBy;

  const handleMetricChange = (patch) => {
    if ('dimension' in patch && patch.dimension !== dimension) {
      onChange({
        ...patch,
        includedValues: [],
        filters: filters.filter((item) => item.field !== patch.dimension),
      });
      return;
    }
    onChange(patch);
  };

  const handleApplyIntervenePreset = (presetKey) => {
    const agentDimension = groupByOptions.find((d) =>
      ['response_agent_username', 'agent_username', 'agent_name', 'agent_user_id', 'agent'].includes(d.id)
    )?.id || 'response_agent_username';

    const responseMetric = metrics.some((m) => m.id === 'avg_agent_response')
      ? 'avg_agent_response'
      : (metrics[0]?.id || 'avg_agent_response');

    if (presetKey === 'benchmark_bar') {
      onChange({
        chartType: 'bar',
        dimension: agentDimension,
        metric: responseMetric,
        detailLevel: 'standard',
        includeSessions: true,
        includedValues: [],
        tableColumns: [],
        reportName: reportName || 'Agent Benchmarking (Response Time)',
      });
    } else if (presetKey === 'benchmark_table') {
      onChange({
        chartType: 'table',
        dimension: agentDimension,
        tableColumns: [agentDimension],
        metric: responseMetric,
        detailLevel: 'standard',
        includeSessions: true,
        includedValues: [],
        reportName: reportName || 'Agent Benchmarking Table',
      });
    } else if (presetKey === 'master') {
      onChange({
        chartType: 'table',
        dimension: agentDimension,
        tableColumns: [agentDimension],
        metric: responseMetric,
        detailLevel: 'master',
        includeSessions: false,
        includedValues: [],
        reportName: reportName || 'Agent Response Master',
      });
    } else if (presetKey === 'overview') {
      onChange({
        chartType: 'number',
        dimension: '',
        metric: responseMetric,
        detailLevel: 'standard',
        includeSessions: true,
        includedValues: [],
        tableColumns: [],
        reportName: reportName || 'Team Response Performance',
      });
    } else if (presetKey === 'trend') {
      onChange({
        chartType: 'line',
        dimension: '',
        metric: responseMetric,
        detailLevel: 'standard',
        includeSessions: true,
        includedValues: [],
        tableColumns: [],
        reportName: reportName || 'Agent Response Time Trend',
      });
    }
  };

  const introSubtitle = datasetLabel
    ? ANALYTICS_COPY.stepConfigureSubtitleWithSource.replace('{source}', datasetLabel)
    : ANALYTICS_COPY.stepConfigureSubtitle;

  const sectionCard = (children) => (
    <Box sx={sx?.sectionCard}>
      {children}
    </Box>
  );
  const interveneStarters = isIntervene ? (
    <Box
      sx={(theme) => ({
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: alpha(theme.palette.primary.main, 0.03),
        p: 2,
      })}
    >
      <Typography variant="subtitle2" fontWeight={700}>
        {ANALYTICS_COPY.interveneStartersTitle}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {ANALYTICS_COPY.interveneStartersSubtitle}
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button
          size="sm"
          variant="outline"
          onClick={() => handleApplyIntervenePreset('benchmark_bar')}
          disabled={!scopeReady}
        >
          ⚡ {ANALYTICS_COPY.intervenePresetAgentBenchmarkBar}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => handleApplyIntervenePreset('benchmark_table')}
          disabled={!scopeReady}
        >
          📋 {ANALYTICS_COPY.intervenePresetAgentBenchmarkTable}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => handleApplyIntervenePreset('master')}
          disabled={!scopeReady}
        >
          🧭 {ANALYTICS_COPY.intervenePresetAgentMaster}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => handleApplyIntervenePreset('overview')}
          disabled={!scopeReady}
        >
          📊 {ANALYTICS_COPY.intervenePresetTeamOverview}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => handleApplyIntervenePreset('trend')}
          disabled={!scopeReady}
        >
          📈 {ANALYTICS_COPY.intervenePresetResponseTrend}
        </Button>
      </Stack>
    </Box>
  ) : null;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.1fr) minmax(340px, 0.9fr)' },
        gap: 2.5,
        alignItems: 'start',
      }}
    >
      <Stack spacing={2} sx={{ order: { xs: 2, lg: 1 }, minWidth: 0 }}>
        <Box sx={sx?.sectionCard}>
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
            {ANALYTICS_COPY.stepConfigure}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {introSubtitle}
          </Typography>
        </Box>

        {interveneStarters ? sectionCard(interveneStarters) : null}

        {usesBusinessScope ? sectionCard(
          scopeLocked ? (
            <ScopeContextBanner
              formName={scopeLabels.formName}
              serviceName={scopeLabels.serviceName}
              businessFunctionName={scopeLabels.businessFunctionName}
              sx={sx}
            />
          ) : (
            <ScopeFields
              dataset={dataset}
              businessFunctions={businessFunctions}
              accountId={accountId}
              campaignIds={campaignIds}
              leadsFormIds={leadsFormIds}
              onAccountChange={(id) => onScopeChange({
                accountId: id,
                campaignIds: [],
                leadsFormIds: [],
              })}
              onCampaignChange={(ids) => onScopeChange({ campaignIds: ids, leadsFormIds: [] })}
              onLeadsFormChange={(ids) => onScopeChange({ leadsFormIds: ids, dimension: '' })}
              compact
            />
          )
        ) : null}

        {sectionCard(
          <Stack spacing={2.5} sx={{ pointerEvents: scopeReady ? 'auto' : 'none', pt:1 }}>
            <VisualizationSelect
              value={chartType}
              dataset={dataset}
              disabled={!scopeReady && usesBusinessScope}
              onChange={handleChartTypeChange}
            />
            <Divider />
            <ReportDateRange
              dateRange={dateRange}
              disabled={!scopeReady}
              rangeHint={
                chartType === 'line' && dateRange?.preset === 'all_time'
                  ? ANALYTICS_COPY.datePresetHintAllTimeLine
                  : isWhatsApp && dateRange?.preset === 'all_time'
                    ? ANALYTICS_COPY.datePresetHintAllTimeWhatsapp
                    : ''
              }
              onChange={(nextRange) => onChange({ dateRange: nextRange })}
            />
            {isWhatsApp ? (
              <>
                <SearchableSelect
                  label="Bot Number"
                  placeholder="All Bot Numbers"
                  options={effectiveBotOptions}
                  value={
                    effectiveBotOptions.find(
                      (b) => String(b.id) === String(botNumber) || String(b.value) === String(botNumber)
                    ) || (botNumber ? { id: String(botNumber), label: String(botNumber) } : null)
                  }
                  onChange={(opt) => {
                    const nextBot = opt?.id || opt?.value ? String(opt.id || opt.value) : '';
                    onChange({ botNumber: nextBot, templateId: '' });
                  }}
                  getOptionLabel={(opt) => opt?.label || String(opt?.id || '')}
                  isOptionEqualToValue={(a, b) => String(a?.id || a?.value) === String(b?.id || b?.value)}
                  loading={isLoadingBots}
                  clearable
                />
                <SearchableSelect
                  label={ANALYTICS_COPY.whatsappTemplateLabel}
                  placeholder={ANALYTICS_COPY.whatsappTemplateAll}
                  options={filteredTemplateOptions}
                  value={selectedTemplate}
                  onChange={(opt) => onChange({ templateId: opt?.id ? String(opt.id) : '' })}
                  getOptionLabel={(opt) => opt?.label || String(opt?.id || '')}
                  isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                  loading={(isPreviewLoading || isLoadingTemplates) && !templateOptions.length}
                  helperText={ANALYTICS_COPY.whatsappTemplateHint}
                  clearable
                />
              </>
            ) : null}
            {isWhatsApp ? null : (
              <>
            <Divider />
            {isIntervene ? (
              <>
                <InterveneAudiencePanel
                  filters={filters}
                  orgId={organisationId}
                  disabled={!scopeReady}
                  onChange={(nextFilters) => onChange({ filters: nextFilters })}
                />
                {chartType === 'number' ? (
                  <Typography variant="body2" color="text.secondary">
                    {ANALYTICS_COPY.interveneNumberHint}
                  </Typography>
                ) : null}
              </>
            ) : null}
            <ReportMetricFields
              embedded
              chartType={chartType}
              metrics={metrics}
              groupByOptions={groupByOptions}
              measureFieldOptions={measureFieldOptions}
              timeFieldOptions={timeFieldOptions}
              needsTableColumns={usesBusinessScope ? needsTableColumns : false}
              tableName={tableName}
              metric={metric}
              aggregation={aggregation}
              measureField={measureField}
              dimension={dimension}
              tableColumns={tableColumns}
              onChange={handleMetricChange}
              disabled={!scopeReady}
              isLoadingFormFields={isLoadingFormFields}
              dataset={dataset}
              campaignIds={campaignIds}
              previewCategoryCount={previewCategoryCount}
            />
            {usesBusinessScope ? (
              <>
                <Divider />
                <ReportFiltersPanel
                  embedded
                  disclosure={filtersCollapsed}
                  title={chartConfig.showGroupBy
                    ? ANALYTICS_COPY.alsoIncludeLabel
                    : ANALYTICS_COPY.includeRowsLabel}
                  subtitle={chartConfig.showGroupBy
                    ? ANALYTICS_COPY.alsoIncludeHint
                    : ANALYTICS_COPY.includeRowsHint}
                  filters={filters}
                  fieldOptions={filterFieldOptions}
                  excludeFieldIds={chartConfig.showGroupBy && dimension ? [dimension] : []}
                  campaignIds={campaignIds}
                  leadsFormIds={leadsFormIds}
                  onChange={(nextFilters) => onChange({ filters: nextFilters })}
                  disabled={!scopeReady}
                />
              </>
            ) : null}
            {isIntervene ? (
              <>
                {chartType === 'table' && detailLevel === 'master' && dateRange?.preset === 'all_time' ? (
                  <Alert severity="warning" sx={{ borderRadius: 1.5 }}>
                    {ANALYTICS_COPY.interveneMasterAllTimeHint}
                  </Alert>
                ) : null}
                <Divider />
                <ReportFiltersPanel
                  embedded
                  disclosure
                  title={ANALYTICS_COPY.interveneAdvancedFiltersTitle}
                  subtitle={ANALYTICS_COPY.interveneAdvancedFiltersHint}
                  filters={advancedFilters}
                  fieldOptions={filterFieldOptions.filter(
                    (item) => !AUDIENCE_FIELD_IDS.includes(item.id)
                  )}
                  dataset={dataset}
                  orgId={organisationId}
                  onChange={(nextAdvanced) => onChange({
                    filters: [
                      ...audienceFilters,
                      ...nextAdvanced,
                    ],
                  })}
                  disabled={!scopeReady}
                />
              </>
            ) : null}
              </>
            )}
          </Stack>
        )}

        {sectionCard(
          <Box sx={{ pointerEvents: scopeReady ? 'auto' : 'none' }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
              {ANALYTICS_COPY.builderStep5Title}
            </Typography>
            <Input
              label={ANALYTICS_COPY.reportNameLabel}
              value={reportName || ''}
              onChange={(e) => onChange({ reportName: e.target.value })}
              disabled={!scopeReady}
              required
            />
          </Box>
        )}
      </Stack>

      <Box
        sx={{
          order: { xs: 1, lg: 2 },
          position: { lg: 'sticky' },
          top: 16,
          minWidth: 0,
          alignSelf: 'start',
          ...sx?.sectionCard,
        }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          useFlexGap
          sx={{ mb: 1.5 }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6 }}>
              {ANALYTICS_COPY.previewSectionTitle}
            </Typography>
            {selectedChart ? (
              <Chip
                size="small"
                variant="outlined"
                label={ANALYTICS_COPY.previewViewChip.replace('{label}', selectedChart.label)}
              />
            ) : null}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            {canExport && preview?.result ? (
              <Button variant="outline" onClick={() => onExport?.(querySpec)}>
                {ANALYTICS_COPY.exportCsv}
              </Button>
            ) : null}
            {onSave ? (
              <Button
                variant="primary"
                disabled={!canSave}
                onClick={onSave}
              >
                {isEditMode ? ANALYTICS_COPY.saveChanges : ANALYTICS_COPY.saveReport}
              </Button>
            ) : null}
          </Stack>
        </Stack>
        <LivePreviewPanel
          title={previewTitle}
          caption={extraFilterCaption}
          flush={chartType === 'table'}
          sx={sx}
        >
          {previewContent()}
        </LivePreviewPanel>
      </Box>
    </Box>
  );
}
